import type {
  MyCandidateFromAPI,
  CandidateStatusEnum,
} from "../../types/candidate";
import { CandidateDetailContent } from "./CandidateDetailContent";
import { ModalCloseButton } from "./CandidateListModal";
import { useToast } from "../../hooks/useToast";
import { Modal } from "../ui/Modal";
import ArrowLeftLinear from "~icons/solar/arrow-left-linear";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CandidateDetailModalProps {
  candidate: MyCandidateFromAPI;
  jdPath?: string;
  /** Subtitle under "Candidate Profile" (v3: jobTitle). */
  jobTitle?: string;
  /** No back arrow — the modal was not opened from a candidates list (v3 hideBackButton). */
  hideBack?: boolean;
  onClose: () => void;
  onStatusChange?: (newStatus: CandidateStatusEnum) => void;
  isStatusUpdating?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Candidate profile stacked on top of the list modal (v3 CandidatesModal,
 * profile view). Mounted only while open, so the Modal is always `open`
 * and closing unmounts it.
 */
export function CandidateDetailModal({
  candidate,
  jdPath,
  jobTitle = "Scouted Candidates",
  hideBack = false,
  onClose,
  onStatusChange,
  isStatusUpdating,
}: CandidateDetailModalProps) {
  const toast = useToast();

  return (
    <Modal open onClose={onClose} title="Candidate Profile" size="2xl" flush hideHeader>
      <div className="flex max-h-[calc(100vh-2rem)] flex-col">
        {/* Header with back + close */}
        <div className="flex shrink-0 items-center justify-between border-b border-black/[0.06] bg-white px-6 py-4">
          <div className="flex items-center gap-3">
            {!hideBack && (
              <button
                type="button"
                onClick={onClose}
                title="Back to candidates list"
                aria-label="Back to candidates list"
                className="flex size-9 cursor-pointer items-center justify-center rounded-full border border-black/10 text-ink shadow-xs transition hover:border-black/20 hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <ArrowLeftLinear aria-hidden="true" className="size-4" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold text-ink">Candidate Profile</h2>
              <p className="text-xs text-ink-muted">{jobTitle}</p>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        {/* Tabs strip + grey body (v3) */}
        <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto">
          <CandidateDetailContent
            layout="modal"
            candidate={candidate}
            jdPath={jdPath}
            onContactAction={toast.info}
            onStatusChange={onStatusChange}
            isStatusUpdating={isStatusUpdating}
          />
        </div>
      </div>
    </Modal>
  );
}
